import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Package, Truck, Trophy, Coins, X, AlertTriangle, Plus, Minus, Search, Crown } from 'lucide-react';
import { API } from '../config';
import { MobileNav } from '../components/MobileNav';
import { useAuth } from '../contexts/AuthContext';

const playfairFont = "'Playfair Display', serif";
const fmt = (n) => new Intl.NumberFormat('id-ID').format(Number(n) || 0);

const TIER_COLORS = {
  gold: '#D4AF37', silver: '#C0C0C0', bronze: '#CD7F32',
  common: '#A3B8AC', last: '#EF4444',
};
const TIER_ORDER = ['all', 'last', 'gold', 'silver', 'bronze', 'common'];
const TIER_LABEL = { all: 'Semua', last: 'Last One', gold: 'Gold', silver: 'Silver', bronze: 'Bronze', common: 'Common' };

const FALLBACK_PRIZE =
  'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 200"><rect fill="%23202824" width="200" height="200"/><text x="100" y="100" fill="%23A3B8AC" font-family="sans-serif" font-size="13" text-anchor="middle" dy=".3em">No image</text></svg>';

const PAGE_SIZE = 50;

const inputStyle = { background: 'rgba(0,0,0,0.25)', border: '1px solid rgba(255,255,255,0.1)', color: '#F8FAF9' };

export const Inventory = () => {
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [groups, setGroups] = useState([]);
  const [tierCounts, setTierCounts] = useState({ all: 0, last: 0, gold: 0, silver: 0, bronze: 0, common: 0 });
  const [totalRaw, setTotalRaw] = useState(0);
  const [totalGroups, setTotalGroups] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [tier, setTier] = useState('all');
  const [sort, setSort] = useState('newest');
  const [search, setSearch] = useState('');
  const [sellGroup, setSellGroup] = useState(null);    // group object → opens sell modal
  const [shipGroup, setShipGroup] = useState(null);    // group object → opens ship modal
  const [submitting, setSubmitting] = useState(false);
  const debounceRef = useRef();

  const fetchPage = useCallback(async (offset = 0) => {
    const params = { limit: PAGE_SIZE, offset, sort };
    if (tier && tier !== 'all') params.tier = tier;
    if (search) params.search = search;
    try {
      const { data } = await axios.get(`${API}/kuji/inventory/grouped`, { params, withCredentials: true });
      setTierCounts(data.tier_counts || {});
      setTotalRaw(data.total_raw_items || 0);
      setTotalGroups(data.total_grouped_items || 0);
      setHasMore(!!data.has_more);
      return data.groups || [];
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal memuat inventory');
      return [];
    }
  }, [tier, sort, search]);

  useEffect(() => {
    setLoading(true);
    fetchPage(0).then((g) => { setGroups(g); setLoading(false); });
  }, [fetchPage]);

  const debounceSearch = (v) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setSearch(v), 350);
  };

  const loadMore = async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    const more = await fetchPage(groups.length);
    setGroups((prev) => [...prev, ...more]);
    setLoadingMore(false);
  };

  const refreshAfterMutation = async () => {
    const fresh = await fetchPage(0);
    setGroups(fresh);
    await checkAuth();
    window.dispatchEvent(new CustomEvent('inventory-updated'));
  };

  const handleSellSubmit = async (qty) => {
    if (!sellGroup) return;
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/kuji/inventory/sell-batch`, {
        event_id: sellGroup.event_id,
        prize_id: sellGroup.prize_id,
        quantity: qty,
      }, { withCredentials: true });
      toast.success(`+${fmt(data.coins_credited)} coins masuk ke wallet`);
      setSellGroup(null);
      await refreshAfterMutation();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal menjual');
    } finally {
      setSubmitting(false);
    }
  };

  const handleShipSubmit = async (qty) => {
    if (!shipGroup) return;
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/kuji/inventory/pick-for-shipping`, {
        event_id: shipGroup.event_id,
        prize_id: shipGroup.prize_id,
        quantity: qty,
      }, { withCredentials: true });
      const ids = data.inventory_ids || [];
      setShipGroup(null);
      navigate('/kuji/shipping/new', { state: { inventoryIds: ids } });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal memilih item');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen pb-28" style={{ background: '#080C0A', fontFamily: "'Outfit', sans-serif" }} data-testid="inventory-page">
      <header className="sticky top-0 z-40 backdrop-blur-2xl" style={{ background: 'rgba(8,12,10,0.92)', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
        <div className="max-w-6xl mx-auto px-5 py-4 flex items-center gap-3">
          <button onClick={() => navigate(-1)} aria-label="Back" data-testid="inventory-back">
            <ArrowLeft className="w-5 h-5" style={{ color: '#F8FAF9' }} />
          </button>
          <h1 className="text-xl md:text-2xl font-light flex-1" style={{ fontFamily: playfairFont, color: '#F8FAF9' }}>Inventori Saya</h1>
          <p className="text-xs hidden sm:block" style={{ color: '#A3B8AC' }}>
            {fmt(totalRaw)} hadiah · {fmt(totalGroups)} jenis
          </p>
        </div>

        {/* Sticky filter tabs */}
        <div className="max-w-6xl mx-auto px-5 pb-3 overflow-x-auto" data-testid="tier-filter-bar">
          <div className="flex gap-2 min-w-max">
            {TIER_ORDER.map((t) => {
              const active = tier === t;
              const c = tierCounts[t] || 0;
              return (
                <button
                  key={t}
                  onClick={() => setTier(t)}
                  className="px-3.5 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all"
                  style={{
                    background: active ? (TIER_COLORS[t] || '#D4AF37') : 'rgba(255,255,255,0.05)',
                    color: active ? '#080C0A' : (TIER_COLORS[t] || '#F8FAF9'),
                    border: `1px solid ${active ? (TIER_COLORS[t] || '#D4AF37') : 'rgba(255,255,255,0.1)'}`,
                  }}
                  data-testid={`tier-filter-${t}`}
                >
                  {TIER_LABEL[t]} <span style={{ opacity: 0.7 }}>· {fmt(c)}</span>
                </button>
              );
            })}
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 py-5">
        {/* Search + Sort */}
        <div className="flex gap-2 mb-5">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: '#A3B8AC' }} />
            <input
              type="text"
              placeholder="Cari hadiah / event…"
              onChange={(e) => debounceSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2.5 rounded-lg outline-none text-sm"
              style={inputStyle}
              data-testid="inventory-search-input"
            />
          </div>
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="px-3 py-2.5 rounded-lg outline-none text-sm"
            style={inputStyle}
            data-testid="inventory-sort-select"
          >
            <option value="newest">Terbaru</option>
            <option value="tier">Tier</option>
            <option value="quantity">Kuantitas</option>
          </select>
        </div>

        {loading && (
          <div className="text-center py-20">
            <div className="inline-block w-12 h-12 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: '#D4AF37', borderTopColor: 'transparent' }} />
          </div>
        )}

        {!loading && groups.length === 0 && (
          <div className="text-center py-20 rounded-2xl backdrop-blur-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }} data-testid="inventory-empty">
            <Package className="w-16 h-16 mx-auto mb-4" style={{ color: '#A3B8AC' }} />
            <p style={{ color: '#A3B8AC' }}>
              {tier === 'all' && !search ? 'Belum ada hadiah di inventori' : `Tidak ada hadiah ${tier !== 'all' ? `tier ${TIER_LABEL[tier]}` : ''}`}
            </p>
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate('/kuji')}
              className="mt-6 px-6 py-3 rounded-full font-semibold"
              style={{ background: '#D4AF37', color: '#080C0A' }}
              data-testid="inventory-empty-cta"
            >
              Lihat Event Kuji
            </motion.button>
          </div>
        )}

        {!loading && groups.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="inventory-grouped-grid">
            {groups.map((g) => (
              <GroupedCard
                key={`${g.event_id}|${g.prize_id}`}
                g={g}
                onSell={() => setSellGroup(g)}
                onShip={() => setShipGroup(g)}
              />
            ))}
          </div>
        )}

        {!loading && hasMore && (
          <div className="text-center mt-6">
            <button
              onClick={loadMore}
              disabled={loadingMore}
              className="px-6 py-2.5 rounded-full font-semibold text-sm disabled:opacity-50"
              style={{ background: 'rgba(212,175,55,0.15)', color: '#D4AF37', border: '1px solid rgba(212,175,55,0.3)' }}
              data-testid="inventory-load-more"
            >
              {loadingMore ? 'Memuat…' : 'Muat Lebih Banyak'}
            </button>
          </div>
        )}
      </main>

      {sellGroup && (
        <QtyModal
          mode="sell"
          group={sellGroup}
          onClose={() => setSellGroup(null)}
          onSubmit={handleSellSubmit}
          submitting={submitting}
        />
      )}

      {shipGroup && (
        <QtyModal
          mode="ship"
          group={shipGroup}
          onClose={() => setShipGroup(null)}
          onSubmit={handleShipSubmit}
          submitting={submitting}
        />
      )}

      <MobileNav />
    </div>
  );
};

// ---------- Grouped card ----------
const GroupedCard = ({ g, onSell, onShip }) => {
  const tierColor = TIER_COLORS[g.prize_tier] || '#F8FAF9';
  const canShip = g.qty_available > 0 && g.is_physical;
  const canSell = g.qty_available > 0 && g.is_physical && g.buyback_coins > 0;
  return (
    <motion.div
      className="rounded-2xl overflow-hidden backdrop-blur-xl"
      style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${tierColor}40` }}
      data-testid={`grouped-card-${g.event_id}-${g.prize_id}`}
    >
      <div className="flex gap-3 p-3">
        <img
          src={g.prize_image || FALLBACK_PRIZE}
          alt={g.prize_name}
          loading="lazy"
          className="w-24 h-24 object-cover rounded-xl flex-shrink-0"
          onError={(e) => { e.target.onerror = null; e.target.src = FALLBACK_PRIZE; }}
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 mb-1">
            {g.prize_tier === 'last' ? <Crown className="w-3.5 h-3.5" style={{ color: tierColor }} /> : <Trophy className="w-3.5 h-3.5" style={{ color: tierColor }} />}
            <span className="text-[10px] tracking-[0.15em] font-semibold" style={{ color: tierColor }}>
              {g.prize_tier?.toUpperCase()}
            </span>
          </div>
          <p className="text-sm font-medium truncate" style={{ color: '#F8FAF9' }}>{g.prize_name}</p>
          <p className="text-[11px] truncate" style={{ color: '#A3B8AC' }}>{g.event_name}</p>
          <div className="mt-2 grid grid-cols-2 gap-x-2 gap-y-0.5 text-[11px]">
            <span style={{ color: '#A3B8AC' }}>Tersedia</span>
            <span className="text-right font-semibold" style={{ color: g.qty_available > 0 ? '#22C55E' : '#A3B8AC' }}>{fmt(g.qty_available)}</span>
            <span style={{ color: '#A3B8AC' }}>Total menang</span>
            <span className="text-right" style={{ color: '#F8FAF9' }}>{fmt(g.qty_total)}</span>
            {g.qty_shipping > 0 && (<>
              <span style={{ color: '#A3B8AC' }}>Dikirim</span>
              <span className="text-right" style={{ color: '#A855F7' }}>{fmt(g.qty_shipping)}</span>
            </>)}
            {g.qty_delivered > 0 && (<>
              <span style={{ color: '#A3B8AC' }}>Diterima</span>
              <span className="text-right" style={{ color: '#22C55E' }}>{fmt(g.qty_delivered)}</span>
            </>)}
            {g.qty_sold_back > 0 && (<>
              <span style={{ color: '#A3B8AC' }}>Sold back</span>
              <span className="text-right" style={{ color: '#9CA3AF' }}>{fmt(g.qty_sold_back)}</span>
            </>)}
          </div>
        </div>
      </div>

      {g.buyback_coins > 0 && (
        <p className="px-3 text-[11px] mb-2" style={{ color: '#A3B8AC' }}>
          Buyback: <span style={{ color: '#D4AF37' }}>{fmt(g.buyback_coins)} coins / item</span>
          {g.qty_available > 0 && (
            <span> · Total: <span style={{ color: '#D4AF37' }}>{fmt(g.qty_available * g.buyback_coins)} coins</span></span>
          )}
        </p>
      )}

      <div className="flex gap-2 p-3 pt-1">
        <button
          onClick={onShip}
          disabled={!canShip}
          className="flex-1 py-2 rounded-full text-xs font-semibold flex items-center justify-center gap-1 disabled:opacity-30"
          style={{ background: 'rgba(212,175,55,0.15)', border: '1px solid rgba(212,175,55,0.4)', color: '#D4AF37' }}
          data-testid={`grouped-ship-btn-${g.event_id}-${g.prize_id}`}
        >
          <Truck className="w-3.5 h-3.5" /> Kirim Barang
        </button>
        <button
          onClick={onSell}
          disabled={!canSell}
          className="flex-1 py-2 rounded-full text-xs font-semibold flex items-center justify-center gap-1 disabled:opacity-30"
          style={{ background: canSell ? 'rgba(34,197,94,0.12)' : 'rgba(255,255,255,0.05)', border: '1px solid rgba(34,197,94,0.4)', color: '#22C55E' }}
          data-testid={`grouped-sell-btn-${g.event_id}-${g.prize_id}`}
        >
          <Coins className="w-3.5 h-3.5" /> Jual Kembali
        </button>
      </div>
    </motion.div>
  );
};

// ---------- Quantity stepper modal (sell or ship) ----------
const QtyModal = ({ mode, group, onClose, onSubmit, submitting }) => {
  const max = group.qty_available;
  const [qty, setQty] = useState(1);
  const dec = () => setQty((q) => Math.max(1, q - 1));
  const inc = () => setQty((q) => Math.min(max, q + 1));
  const total = qty * (group.buyback_coins || 0);
  const isSell = mode === 'sell';
  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.7)' }} data-testid={isSell ? 'sell-modal' : 'ship-modal'}>
      <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="rounded-2xl p-6 max-w-md w-full backdrop-blur-2xl" style={{ background: '#0c1f17', border: '1px solid rgba(212,175,55,0.3)' }}>
        <div className="flex justify-between items-start mb-4">
          <div className="flex items-center gap-2">
            {isSell ? <AlertTriangle className="w-5 h-5" style={{ color: '#EAB308' }} /> : <Truck className="w-5 h-5" style={{ color: '#D4AF37' }} />}
            <h3 className="text-xl font-light" style={{ fontFamily: playfairFont, color: '#F8FAF9' }}>
              {isSell ? 'Jual kembali hadiah ini?' : 'Kirim hadiah ini'}
            </h3>
          </div>
          <button onClick={onClose}><X className="w-5 h-5" style={{ color: '#A3B8AC' }} /></button>
        </div>

        <div className="rounded-xl p-4 mb-4" style={{ background: 'rgba(255,255,255,0.04)' }}>
          <p className="text-xs" style={{ color: '#A3B8AC' }}>Hadiah</p>
          <p className="font-medium mb-2" style={{ color: '#F8FAF9' }}>{group.prize_name}</p>
          <p className="text-xs" style={{ color: '#A3B8AC' }}>Event</p>
          <p className="text-sm mb-3" style={{ color: '#CFE0D5' }}>{group.event_name}</p>

          <p className="text-xs mb-1.5" style={{ color: '#A3B8AC' }}>Jumlah (max {fmt(max)})</p>
          <div className="flex items-center gap-2">
            <button onClick={dec} disabled={qty <= 1} className="w-10 h-10 rounded-full flex items-center justify-center disabled:opacity-30" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.15)' }} data-testid="qty-dec">
              <Minus className="w-4 h-4" style={{ color: '#F8FAF9' }} />
            </button>
            <input
              type="number"
              min={1}
              max={max}
              value={qty}
              onChange={(e) => {
                const v = parseInt(e.target.value, 10);
                if (isNaN(v)) return;
                setQty(Math.max(1, Math.min(max, v)));
              }}
              className="flex-1 rounded-full py-2 text-center text-base font-semibold outline-none"
              style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.1)', color: '#F8FAF9' }}
              data-testid="qty-input"
            />
            <button onClick={inc} disabled={qty >= max} className="w-10 h-10 rounded-full flex items-center justify-center disabled:opacity-30" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.15)' }} data-testid="qty-inc">
              <Plus className="w-4 h-4" style={{ color: '#F8FAF9' }} />
            </button>
          </div>
          <div className="flex justify-between gap-2 mt-2">
            {[1, 5, 10, Math.min(max, 50), max].filter((v, i, a) => a.indexOf(v) === i && v >= 1 && v <= max).map((v) => (
              <button key={v} onClick={() => setQty(v)} className="flex-1 text-[10px] py-1 rounded-full" style={{ background: 'rgba(255,255,255,0.04)', color: '#A3B8AC', border: '1px solid rgba(255,255,255,0.1)' }} data-testid={`qty-quick-${v}`}>
                {v === max ? `Max ${fmt(v)}` : `${v}×`}
              </button>
            ))}
          </div>

          {isSell && (
            <div className="mt-3 pt-3" style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}>
              <p className="text-xs" style={{ color: '#A3B8AC' }}>Coin per item</p>
              <p className="text-sm font-medium mb-2" style={{ color: '#D4AF37' }}>{fmt(group.buyback_coins)} Coin <span className="text-[11px]" style={{ color: '#A3B8AC' }}>(≈ Rp{fmt(group.buyback_coins * 1000)})</span></p>
              <p className="text-xs" style={{ color: '#A3B8AC' }}>Total coin diterima</p>
              <p className="text-2xl font-light" style={{ color: '#D4AF37', fontFamily: playfairFont }}>{fmt(total)} Coin</p>
              <p className="text-xs" style={{ color: '#A3B8AC' }}>≈ Rp{fmt(total * 1000)}</p>
            </div>
          )}
        </div>

        {isSell && <p className="text-xs mb-4" style={{ color: '#EF4444' }}>Aksi ini tidak bisa dibatalkan.</p>}

        <div className="flex gap-3">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-full text-sm font-medium" style={{ background: 'rgba(255,255,255,0.06)', color: '#F8FAF9' }} data-testid="qty-cancel-btn">
            Batal
          </button>
          <button
            onClick={() => onSubmit(qty)}
            disabled={submitting || qty < 1 || qty > max}
            className="flex-1 py-2.5 rounded-full text-sm font-semibold disabled:opacity-50"
            style={{ background: '#D4AF37', color: '#080C0A' }}
            data-testid="qty-confirm-btn"
          >
            {submitting ? 'Memproses…' : (isSell ? 'Jual Kembali' : 'Lanjut ke Pengiriman')}
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default Inventory;
